/**
 * Multi-School Coverage Table
 *
 * Shows, for each selected school, which of the four stored single-school
 * summary types (geographic/competitive/projections/combined) are available
 * at the current drive time/travel mode. Used so an admin can see up front
 * which schools will be skipped when generating a multi-school rollup.
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Room as RoomIcon
} from '@mui/icons-material';

const TYPE_COLUMNS = [
  { key: 'geographic', label: 'Geographic' },
  { key: 'competitive', label: 'Competitive' },
  { key: 'projections', label: 'Projections' },
  { key: 'combined', label: 'Overview (single-school)' }
];

const CoverageCell = ({ available, isDropAPin, typeKey }) => {
  // Drop-a-pin locations only ever have a geographic summary by design.
  if (isDropAPin && typeKey !== 'geographic') {
    return (
      <Tooltip title="Drop-a-pin locations only have geographic summaries">
        <RoomIcon fontSize="small" color="disabled" />
      </Tooltip>
    );
  }
  return available
    ? <CheckCircleIcon fontSize="small" color="success" />
    : <CancelIcon fontSize="small" color="disabled" />;
};

const MultiSchoolCoverageTable = ({ schools, coverage, driveTime, travelMode, loading }) => {
  if (!schools || schools.length === 0) return null;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          Source Coverage
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={`${driveTime}min · ${travelMode === 'walk' ? 'Walking' : 'Driving'}`}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Schools missing a summary type below will be skipped when generating that rollup.
        Generate the missing per-school summary first (in the single-school "Generate &amp; View" tab) to include it.
      </Typography>
      <TableContainer sx={{ opacity: loading ? 0.6 : 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>School</strong></TableCell>
              {TYPE_COLUMNS.map(col => (
                <TableCell key={col.key} align="center"><strong>{col.label}</strong></TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {schools.map(school => {
              const info = coverage?.[school.uuid];
              const isDropAPin = info ? info.is_drop_a_pin : !school.ncessch;
              const found = info ? info.found : true;
              return (
                <TableRow key={school.uuid}>
                  <TableCell>
                    {school.name}
                    {!found && (
                      <Chip size="small" color="warning" variant="outlined" label="Not found" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  {TYPE_COLUMNS.map(col => (
                    <TableCell key={col.key} align="center">
                      <CoverageCell
                        available={!!info?.types?.[col.key]}
                        isDropAPin={isDropAPin}
                        typeKey={col.key}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default MultiSchoolCoverageTable;
